import { useState, useCallback } from 'react';
import { useMonitoringGoods } from './MonitoringContext';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';

import ArrowRedUpIcon from '@/assets/icons/V3/arrow/ArrowRedUpIcon.svg?react';
import ArrowBlueDownIcon from '@/assets/icons/V3/arrow/ArrowBlueDownIcon.svg?react';
import { ImageIcon } from 'lucide-react';
import EditKeyword from './EditKeyword';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MOCK_GOODS } from './_mock/mockGoods';
import { usePost, CPLAT_API_ROUTES } from '@/api';

import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { MonitoringGoodsDetail } from '@/types/api';
import DeleteGoodsBtn from './DeleteGoodsBtn';

const MonitoringGoodsList = () => {
  const { goods, isLoading, isError, refetch } = useMonitoringGoods();
  const isPreviewMode = useMembershipPreviewMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();
  const user = useAppSelector((state) => state.user);
  // 카드 클릭 핸들러
  const handleCardClick = (GoodsNo: string) => {
    // 기존 쿼리 유지 + select만 변경
    const params = new URLSearchParams(searchParams);
    params.set('select', GoodsNo);
    navigate({ search: params.toString() });
  };

  const { mutate: setMonitoringGoods } = usePost(CPLAT_API_ROUTES.setUserMonitoringGoods);

  const handleDelete = (goods: MonitoringGoodsDetail) => {
    setIsDeleting(true);
    setMonitoringGoods(
      {
        cplatToken: user.CplatToken,
        GoodsNo: goods.GoodsNo,
        NoticeYn: goods.NoticeYn,
        StoreName: goods.StoreName,
        UseYn: 'N',
        InsertYn: 'N',
      },
      {
        onSuccess: (res) => {
          const { code } = res.data;

          if (code === '200') {
            showToast('success', '상품이 삭제되었습니다.');
            refetch();
          } else {
            showToast('warning', res.data.message || '상품 삭제에 실패했습니다.');
          }
          setIsDeleting(false);
        },
        onError: () => {
          showToast('warning', '상품 삭제에 실패했습니다. 다시 시도해주세요.');
        },
        onSettled: () => {
          setIsDeleting(false);
        },
      },
    );
  };

  if (isLoading) return <div>로딩 중...</div>;
  if (isError) return <div>에러 발생</div>;

  // preview 또는 빈 목록에서는 Figma 기준 예시 상품을 보여준다.
  const listToShow = isPreviewMode || goods.length === 0 ? MOCK_GOODS : goods;
  return (
    <div className="grid h-full w-full grid-cols-1 gap-5 bg-gr-50 min-[1200px]:grid-cols-2">
      {listToShow.map((item) => {
        const latestDaily = item.MonitoringGoodsDetailDailyList?.[0];
        const registeredKeywords = item.RelatedKeyword || [];
        const keywordRanks = latestDaily?.RelatedKeywordsRanking || [];

        const sortedKeywords = registeredKeywords
          .map((keyword, idx) => {
            const rankInfo = keywordRanks.find((k) => k.Keyword === keyword) || {
              Keyword: keyword,
              Rank: -1,
              RankingChangedRange: 0,
              RankingChangedStatus: 'stay',
              PagePosition: '',
            };
            return {
              ...rankInfo,
              _regOrder: idx,
              _changeAbs: Math.abs(rankInfo.RankingChangedRange ?? 0),
            };
          })
          .sort((a, b) => {
            if (b._changeAbs !== a._changeAbs) {
              return b._changeAbs - a._changeAbs;
            }
            return a._regOrder - b._regOrder;
          });

        // 판매 중지 여부
        const isNotSell = item.IsDisplayStopped;
        const isMockGoods = isPreviewMode || goods.length === 0;

        return (
          <div
            key={item.GoodsNo}
            data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
            className="relative flex cursor-pointer gap-3 rounded-xl border bg-white p-4"
            // onClick={() => handleCardClick(item.GoodsNo)}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("[data-state='open']") ||
                target.closest("[data-state='closed']")
              ) {
                return;
              }
              handleCardClick(item.GoodsNo);
            }}
          >
            {/* 오버레이 */}
            {isNotSell && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/80">
                <span className="text-point-500 Body2M16">판매가 중지된 상품입니다</span>
              </div>
            )}

            {/* 상품 이미지 */}
            <GoodsImage src={item.GoodsImageUrl} alt={item.GoodsName} />
            {/* 상품 정보 */}
            <div className="flex min-w-0 flex-1 flex-col gap-2 ">
              <div className="flex flex-col gap-[12px]">
                {/* 스토어명, 알림톡 뱃지 */}
                <div className="flex items-center gap-[5px]">
                  {item.StoreName && (
                    <span className="rounded-[8px] bg-point-100 px-[6px] py-[2px] text-point-500 Caption1M12">
                      {item.StoreName}
                    </span>
                  )}
                  {item.NoticeYn === 'Y' && (
                    <span className="rounded-[8px] bg-gr-100 px-[6px] py-[2px] text-gr-600 Caption1M12">
                      알림톡 수신중
                    </span>
                  )}
                </div>
                {/* 상품명 */}
                <div className="truncate text-gr-800  Body1S16">{item.GoodsName}</div>
                {/* 등록일, 키워드 개수 */}
                <div className="flex gap-2 text-gr-600 Caption1M12">
                  <div>
                    {item.GoodsRegisterDate
                      ? item.GoodsRegisterDate.slice(0, 10).replace(/-/g, '.')
                      : '-'}{' '}
                    업데이트
                  </div>
                  <div>
                    추적 키워드
                    <span className="ml-1 text-gr-700">{registeredKeywords.length}개</span>
                  </div>
                </div>
              </div>
              {/* 키워드별 순위 (정렬된 2개만) */}
              <div className="space-y-1">
                {sortedKeywords.slice(0, 2).map((kw) => (
                  <div key={kw.Keyword} className="flex w-full items-center gap-2 Body5M14">
                    <div className="text-gr-800">{kw.Keyword}</div>
                    <span className="flex-1 text-right text-gr-800">
                      {kw.Rank > 0 ? `${kw.Rank}위` : '1000위 밖'}
                    </span>
                    <div className="min-w-[30px]">
                      {kw.RankingChangedStatus === 'up' && (
                        <span className="ml-2 flex items-center text-statusRed-400">
                          <ArrowRedUpIcon className="mr-1 " />
                          {Math.abs(kw.RankingChangedRange)}
                        </span>
                      )}
                      {kw.RankingChangedStatus === 'down' && (
                        <span className="flex items-center text-statusBlue-400">
                          <ArrowBlueDownIcon className="mr-1 " />
                          {Math.abs(kw.RankingChangedRange)}
                        </span>
                      )}
                      {(!kw.RankingChangedStatus || kw.RankingChangedStatus === 'stay') && (
                        <span className="ml-2 text-gr-400">-</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex  gap-2">
              {/* 키워드 관리 버튼 */}
              <EditKeyword goods={item} isMockGoods={isMockGoods} />
              <DeleteGoodsBtn
                isLoading={isDeleting}
                isMockGoods={isMockGoods}
                handleDelete={() => handleDelete(item)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MonitoringGoodsList;

const GoodsImage = ({ src, alt }: { src?: string; alt?: string }) => {
  const [hasError, setHasError] = useState(!src);
  const handleError = useCallback(() => setHasError(true), []);

  if (hasError) {
    return (
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[8px] border border-gr-100 bg-gr-50">
        <ImageIcon className="h-8 w-8 text-gr-300" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={handleError}
      className="h-20 w-20 shrink-0 rounded-[8px] border border-gr-100 object-cover"
    />
  );
};
